import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../App';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  User, Star, CheckCircle, Clock, MapPin, Gauge, Camera, ShieldCheck, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const vehicleLabels = { carro: 'Carro', moto: 'Moto', sem_veiculo: 'Sem veículo' };

const MetricCard = ({ icon: Icon, label, value, color, testId }) => {
  const colors = {
    green: 'bg-green-50 text-green-600',
    blue: 'bg-blue-50 text-blue-600',
    purple: 'bg-purple-50 text-purple-600',
    amber: 'bg-amber-50 text-amber-600'
  };
  return (
    <Card className="border-0 shadow-sm" data-testid={testId}>
      <CardContent className="p-3.5">
        <div className={`w-8 h-8 rounded-lg ${colors[color]} flex items-center justify-center mb-2`}>
          <Icon className="w-4 h-4" />
        </div>
        <p className="text-lg font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-400">{label}</p>
      </CardContent>
    </Card>
  );
};

const InfoRow = ({ label, value }) => (
  <div>
    <p className="text-xs text-slate-400">{label}</p>
    <p className="font-medium text-slate-800">{value}</p>
  </div>
);

const parseDateOnly = (dateStr) => {
  const match = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [, y, mo, d] = match;
  return { year: parseInt(y), month: parseInt(mo), day: parseInt(d) };
};

const formatDateOnly = (dateStr) => {
  const parsed = parseDateOnly(dateStr);
  if (!parsed) return '-';
  return `${String(parsed.day).padStart(2, '0')}/${String(parsed.month).padStart(2, '0')}/${parsed.year}`;
};

const calculateAge = (dob) => {
  const parsed = parseDateOnly(dob);
  if (!parsed) return null;
  const today = new Date();
  let age = today.getFullYear() - parsed.year;
  const m = (today.getMonth() + 1) - parsed.month;
  if (m < 0 || (m === 0 && today.getDate() < parsed.day)) age--;
  return age;
};

export const PrestadorProfileView = ({ prestadorId, mode = 'own' }) => {
  const { token } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showTerms, setShowTerms] = useState(false);
  const [termsText, setTermsText] = useState('');
  const [termsRead, setTermsRead] = useState(false);
  const [accepting, setAccepting] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const fileInputRef = useRef(null);
  const isOwn = mode === 'own';
  const headers = { Authorization: `Bearer ${token}` };

  const fetchProfile = async () => {
    try {
      const url = isOwn ? `${API}/api/prestador/profile` : `${API}/api/prestador/profile/${prestadorId}`;
      const res = await fetch(url, { headers });
      const data = await res.json();
      if (res.ok) setProfile(data);
    } catch (e) {}
    setLoading(false);
  };

  useEffect(() => { fetchProfile(); }, [prestadorId]);

  useEffect(() => {
    if (!isOwn || !navigator.geolocation) return;
    const sendLocation = () => {
      navigator.geolocation.getCurrentPosition(async (pos) => {
        try {
          await fetch(`${API}/api/prestador/profile/location`, {
            method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
            body: JSON.stringify({ lat: pos.coords.latitude, lng: pos.coords.longitude })
          });
        } catch (e) {}
      }, () => {}, { timeout: 8000 });
    };
    sendLocation();
    const interval = setInterval(sendLocation, 3 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOwn, token]);

  const openTerms = async () => {
    try {
      const res = await fetch(`${API}/api/prestador/terms`, { headers });
      const data = await res.json();
      setTermsText(data.content || '');
      setTermsRead(false);
      setShowTerms(true);
    } catch (e) { toast.error('Erro ao carregar termo'); }
  };

  const handleTermsScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 30) setTermsRead(true);
  };

  const acceptTerms = async () => {
    setAccepting(true);
    try {
      const res = await fetch(`${API}/api/prestador/profile/accept-terms`, { method: 'POST', headers });
      if (res.ok) {
        toast.success('Termo aceito com sucesso!');
        setShowTerms(false);
        fetchProfile();
      }
    } catch (e) { toast.error('Erro ao aceitar termo'); }
    setAccepting(false);
  };

  const handlePhotoChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('photo', file);
      const res = await fetch(`${API}/api/prestador/profile/photo`, { method: 'POST', headers, body: formData });
      if (res.ok) { toast.success('Foto atualizada!'); fetchProfile(); }
    } catch (e) { toast.error('Erro ao enviar foto'); }
    setUploadingPhoto(false);
  };

  const toggleAvailability = async () => {
    if (!isOwn) return;
    setTogglingStatus(true);
    try {
      const newStatus = profile.availability_status === 'disponivel' ? 'indisponivel' : 'disponivel';
      const res = await fetch(`${API}/api/prestador/profile/availability-status`, {
        method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: newStatus })
      });
      if (res.ok) {
        setProfile(p => ({ ...p, availability_status: newStatus }));
        toast.success(newStatus === 'disponivel' ? 'Você está Disponível' : 'Você está Indisponível');
      }
    } catch (e) { toast.error('Erro'); }
    setTogglingStatus(false);
  };

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  if (!profile) return <div className="text-center py-20 text-slate-400">Perfil não encontrado</div>;

  const finishedStatuses = ['finalizada', 'aguardando_validacao', 'aprovado_faturamento', 'faturado'];

  return (
    <div className="space-y-4" data-testid="prestador-profile-view">
      <Card className="border-0 shadow-sm overflow-hidden">
        <div className="bg-gradient-to-br from-slate-900 to-slate-800 p-5 text-white">
          <div className="flex items-center gap-4">
            <div className="relative">
              <div className="w-16 h-16 rounded-full bg-white/10 border-2 border-white/20 flex items-center justify-center overflow-hidden">
                {profile.profile_photo_url ? (
                  <img src={`${API}${profile.profile_photo_url}`} alt={profile.name} className="w-full h-full object-cover" />
                ) : (
                  <User className="w-8 h-8 text-white/70" />
                )}
              </div>
              {isOwn && (
                <button onClick={() => fileInputRef.current?.click()}
                  className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-amber-500 flex items-center justify-center border-2 border-slate-900"
                  data-testid="upload-photo-btn">
                  {uploadingPhoto ? <Loader2 className="w-3 h-3 animate-spin" /> : <Camera className="w-3 h-3" />}
                </button>
              )}
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} data-testid="profile-photo-input" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-bold text-lg leading-tight truncate">{profile.name}</p>
              <p className="text-xs text-slate-300">{profile.supplier_name}</p>
              <div className="flex items-center gap-1 mt-1">
                <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                <span className="text-sm font-semibold">{(profile.rating_avg || 0).toFixed(1)}</span>
                <span className="text-xs text-slate-400">({profile.rating_count || 0} avaliações)</span>
              </div>
            </div>
          </div>

          <div className="mt-4 flex items-center justify-between bg-white/10 rounded-xl px-3 py-2.5">
            <div className="flex items-center gap-2">
              <span className={`w-2 h-2 rounded-full ${profile.availability_status === 'disponivel' ? 'bg-green-400 animate-pulse' : 'bg-slate-400'}`} />
              <span className="text-sm font-medium">{profile.availability_status === 'disponivel' ? 'Disponível' : 'Indisponível'}</span>
            </div>
            {isOwn && (
              <Switch checked={profile.availability_status === 'disponivel'} onCheckedChange={toggleAvailability} disabled={togglingStatus} data-testid="availability-status-switch" />
            )}
          </div>
        </div>
      </Card>

      {isOwn && !profile.terms_accepted && (
        <Card className="border border-amber-200 bg-amber-50 shadow-sm" data-testid="terms-pending-card">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">Complete seu cadastro</p>
                <p className="text-xs text-amber-700 mt-0.5">Leia e aceite o Termo de Uso e Responsabilidade para continuar atendendo missões.</p>
                <Button size="sm" onClick={openTerms} className="mt-2 h-8 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs" data-testid="open-terms-btn">
                  Ler e Aceitar Termo
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
      {isOwn && profile.terms_accepted && (
        <div className="flex items-center gap-1.5 text-xs text-green-600 px-1" data-testid="terms-accepted-label">
          <CheckCircle className="w-3.5 h-3.5" /> Termo aceito em {new Date(profile.terms_accepted_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}
        </div>
      )}

      <div className="grid grid-cols-2 gap-3">
        <MetricCard icon={CheckCircle} label="Cumprimento de Missões" value={`${profile.completion_rate ?? 0}%`} color="green" testId="metric-completion" />
        <MetricCard icon={Clock} label="SLA" value={profile.sla_percent != null ? `${profile.sla_percent}%` : 'N/A'} color="blue" testId="metric-sla" />
        <MetricCard icon={Gauge} label="Taxa de Disponibilidade" value={`${profile.availability_rate ?? 0}%`} color="purple" testId="metric-availability" />
        <MetricCard icon={Star} label="Nota Média" value={(profile.rating_avg || 0).toFixed(1)} color="amber" testId="metric-rating" />
      </div>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Dados Cadastrais</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <InfoRow label="Sexo" value={profile.sexo || '-'} />
            <InfoRow label="Data de Nascimento" value={profile.data_nascimento ? `${formatDateOnly(profile.data_nascimento)} (${calculateAge(profile.data_nascimento)} anos)` : '-'} />
            <InfoRow label="Agente armado" value={profile.agente_armado ? 'Sim' : 'Não'} />
            <InfoRow label="Veículo" value={vehicleLabels[profile.vehicle_type] || '-'} />
            <InfoRow label="Função de Segurança" value={profile.funcao_seguranca ? (profile.funcao_seguranca_tipo || 'Sim') : 'Não'} />
            {profile.funcao_seguranca && <InfoRow label="Função Vigente/Ativa" value={profile.funcao_seguranca_ativo ? 'Sim' : 'Não'} />}
            <InfoRow label="Antenista" value={profile.antenista ? 'Sim' : 'Não'} />
            <InfoRow label="Telefone" value={profile.phone || '-'} />
            <InfoRow label="Email" value={profile.email || '-'} />
          </div>
          {profile.emergency_contacts?.length > 0 && (
            <div className="mt-3 pt-3 border-t">
              <p className="text-xs text-slate-400 mb-1.5">Contatos de Emergência</p>
              {profile.emergency_contacts.map((c, i) => (
                <p key={i} className="text-sm text-slate-700">{c.name} — {c.phone}</p>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-slate-400 uppercase mb-2">Localização Atual</p>
          {profile.current_lat ? (
            <>
              <a href={`https://www.google.com/maps?q=${profile.current_lat},${profile.current_lng}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-sm text-blue-600 font-medium hover:underline" data-testid="current-location-link">
                <MapPin className="w-4 h-4" /> Ver no Google Maps
              </a>
              <p className="text-xs text-slate-400 mt-1">Atualizado em {new Date(profile.location_updated_at).toLocaleString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</p>
            </>
          ) : (
            <p className="text-sm text-slate-400">Localização não disponível</p>
          )}
        </CardContent>
      </Card>

      <Card className="border-0 shadow-sm">
        <CardContent className="p-4">
          <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Histórico de Missões ({profile.mission_history?.length || 0})</p>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {(profile.mission_history || []).length === 0 && <p className="text-sm text-slate-400">Nenhuma missão atribuída ainda</p>}
            {(profile.mission_history || []).map(m => (
              <div key={m.id} className="flex items-center justify-between p-2.5 bg-slate-50 rounded-lg" data-testid={`profile-mission-${m.id}`}>
                <div>
                  <p className="text-sm font-medium text-slate-800">{m.vehicle_plate || m.code}</p>
                  <p className="text-xs text-slate-400">{new Date(m.created_at).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</p>
                </div>
                <Badge className={finishedStatuses.includes(m.status) ? 'bg-green-100 text-green-700' : m.status === 'cancelada' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}>
                  {m.status}
                </Badge>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {profile.ratings?.length > 0 && (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase mb-3">Avaliações Recebidas</p>
            <div className="space-y-2">
              {profile.ratings.map(r => (
                <div key={r.id} className="p-2.5 bg-slate-50 rounded-lg">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map(i => <Star key={i} className={`w-3.5 h-3.5 ${i <= r.rating ? 'text-amber-400 fill-amber-400' : 'text-slate-200'}`} />)}
                  </div>
                  {r.comment && <p className="text-xs text-slate-600 mt-1">{r.comment}</p>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <Dialog open={showTerms} onOpenChange={setShowTerms}>
        <DialogContent className="max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader><DialogTitle>Termo de Uso e Responsabilidade</DialogTitle></DialogHeader>
          <div className="flex-1 overflow-y-auto border rounded-lg p-4 bg-slate-50 text-sm whitespace-pre-wrap leading-relaxed" onScroll={handleTermsScroll} data-testid="terms-scroll-area">
            {termsText}
          </div>
          <div className="pt-2">
            {!termsRead && <p className="text-xs text-amber-600 mb-2">Role até o final do documento para habilitar o aceite.</p>}
            <Button onClick={acceptTerms} disabled={!termsRead || accepting} className="w-full bg-slate-900 hover:bg-slate-800" data-testid="accept-terms-btn">
              {accepting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Li e Aceito os Termos'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrestadorProfileView;
